import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useMediaQuery } from 'react-responsive';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { Grid, LinearProgress, useTheme, Box, Paper } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';

import { Intro } from '../../components/ManageCollection/ManageCollection.styles';
import ClientsIcon from '../../assets/icons/clients.svg';
import WPIcon from '../../assets/icons/whatsapp-primary-dark.svg';

import { errorToast } from '../../constants.js/misc';

import { routes } from '../../constants.js/misc';
import { setClientToEdit } from '../../modules/clients';
import { getClientService } from '../../services/clients';
import ClientStatus from '../../components/ClientInfoPage/ClientStatus/ClientStatus';
import ClientOrders from '../../components/ClientInfoPage/ClientOrders/ClientOrders';
import ClientInfo from '../../components/ClientInfoPage/ClientInfo/ClientInfo';
import ClientNotes from '../../components/ClientInfoPage/ClientNotes/ClientNotes';
import StatePill from '../../components/StatePill';
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';

/**
 * Renders the order info page
 * @param {number} storeId main store id
 */
export const ClientInfoPage = ({ storeId, clientToEdit, setClientToEdit, storeData }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();
    const theme = useTheme();
    let { id } = useParams();

    // Enhanced responsive breakpoints
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 600px)' });
    const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
    const isSmallMobile = useMediaQuery({ query: '(max-width: 360px)' });
    const isTablet = useMediaQuery({ query: '(min-width: 601px) and (max-width: 960px)' });

    // State variables
    const [gettingClient, setGettingClient] = useState(false);
    const [clientData, setClientData] = useState(null);

    /**
     * Get the product data if an id is provided in the url
     */
    useEffect(() => {
        if (id) {
            /**
             * If the clientToEdit variable is set in the state use it to
             * show the info
             */
            if (clientToEdit) {
                setClientData(clientToEdit);
                setGettingClient(true);
                getClientService(storeId, id)
                    .then((res) => {
                        setGettingClient(false);
                        setClientData(res.data);
                    })
                    .catch((err) => {
                        setGettingClient(false);
                        enqueueSnackbar(t('OrderDetail.dataError'), errorToast);
                    });
            } else {
                /**
                 * If the variable is not set it means the user got to the page using
                 * only the url, get the store data from the api to show the info
                 */
                setGettingClient(true);
                getClientService(storeId, id)
                    .then((res) => {
                        setGettingClient(false);
                        setClientData(res.data);
                    })
                    .catch((err) => {
                        setGettingClient(false);
                        enqueueSnackbar(t('OrderDetail.dataError'), errorToast);
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /**
     * Clean the saved product data after unmount
     */
    // useEffect(() => () => setClientToEdit(null), []);

    const message = `Hola, ${clientData?.first_name} ${clientData?.last_name},
Te contactamos de ${storeData?.name}
`;

    const handleClickContact = () => {
        const messageUrl = `https://wa.me/${clientData?.phone_number}?text=`;
        window.open(`${messageUrl}${encodeURIComponent(message).replace(/%20/g, '+')}`);
    };

    // Responsive styles
    const getHeaderGridProps = () => {
        if (isMobile) {
            return { xs: 12 }; // Stack vertically on mobile
        } else if (isTabletOrMobile) {
            return { xs: 4, md: 7 }; // Original tablet layout
        } else {
            return { xs: 4, md: 7 }; // Desktop layout
        }
    };

    const getActionButtonsGridProps = () => {
        if (isMobile) {
            return { xs: 6 }; // Half width for each button on mobile (handled in mobile section)
        } else if (isTabletOrMobile) {
            return { xs: 6, sm: 4, md: 3 }; // Responsive button sizing for tablet
        } else {
            return { xs: 6, sm: 4, md: 3, lg: 2 }; // Desktop layout with better responsiveness
        }
    };

    const getButtonSize = () => {
        if (isSmallMobile) return 'small';
        if (isMobile) return 'medium';
        return 'medium';
    };

    const getButtonStyles = () => ({
        whiteSpace: 'nowrap',
        fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
        padding: isSmallMobile ? '4px 8px' : isMobile ? '6px 12px' : '8px 16px',
        minWidth: isSmallMobile ? 'auto' : 'unset',
    });

    const getTitleVariant = () => {
        if (isSmallMobile) return 'h6';
        if (isMobile) return 'h5';
        if (isTabletOrMobile) return 'h4';
        return 'h4';
    };

    const getContainerSpacing = () => {
        if (isMobile) return 1;
        if (isTabletOrMobile) return 2;
        return 3;
    };

    const getMarginStyle = () => {
        if (isMobile) return { margin: 5 };
        if (isTabletOrMobile) return { margin: 10 };
        return { margin: 0 };
    };

    return (
        <Grid container spacing={getContainerSpacing()} style={{ paddingTop: isMobile ? 56 : 24 }}>
            {isTabletOrMobile && (
                <Intro item container xs={12}>
                    <img
                        src={ClientsIcon}
                        alt="ClientsIcon"
                        style={{
                            width: isMobile ? '20px' : '24px',
                            height: isMobile ? '20px' : '24px',
                        }}
                    />
                    <Typography
                        variant={isMobile ? 'body2' : 'caption'}
                        style={{ marginLeft: '8px' }}
                    >
                        {t('ClientTitle')}
                    </Typography>
                </Intro>
            )}

            <Grid item xs={12} style={getMarginStyle()}>
                <Grid item xs={12}>
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        spacing={isMobile ? 1 : 0}
                    >
                        {/* Client Name and Pills Section */}
                        <Grid item xs={12} lg={7} {...getHeaderGridProps()}>
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    flexWrap: 'wrap',
                                    marginBottom: isMobile ? '16px' : '10px',
                                    gap: isMobile ? '8px' : '6px',
                                }}
                            >
                                <Typography
                                    variant={getTitleVariant()}
                                    style={{
                                        marginRight: isMobile ? '0' : '6px',
                                        wordBreak: 'break-word',
                                        lineHeight: isMobile ? '1.2' : '1.4',
                                    }}
                                >
                                    {clientData
                                        ? `${clientData?.first_name} ${clientData?.last_name}`
                                        : ''}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap="4px">
                                    {clientData?.lead && (
                                        <StatePill state={clientData?.lead?.name} type={1} />
                                    )}
                                    {clientData?.frequency && (
                                        <StatePill state={clientData?.frequency?.name} type={2} />
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Action Buttons Section */}
                        {isMobile ? (
                            // Mobile: Stack buttons in a row
                            <Grid item xs={12}>
                                <Grid container spacing={1} justifyContent="center">
                                    <Grid item xs={6}>
                                        <Link
                                            component={RouterLink}
                                            to={routes.createOrder}
                                            style={{
                                                textDecoration: 'none',
                                                width: '100%',
                                                display: 'block',
                                            }}
                                        >
                                            <Button
                                                type="button"
                                                variant="contained"
                                                color="primary"
                                                size={getButtonSize()}
                                                style={{ ...getButtonStyles(), width: '100%' }}
                                                startIcon={
                                                    <AddOutlinedIcon
                                                        style={{
                                                            fontSize: isSmallMobile
                                                                ? '16px'
                                                                : '20px',
                                                        }}
                                                    />
                                                }
                                            >
                                                {isSmallMobile
                                                    ? t('Order.create').substring(0, 6) + '...'
                                                    : t('Order.create')}
                                            </Button>
                                        </Link>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            type="button"
                                            variant="contained"
                                            onClick={handleClickContact}
                                            color="primary"
                                            size={getButtonSize()}
                                            style={{ ...getButtonStyles(), width: '100%' }}
                                            startIcon={
                                                <img
                                                    src={WPIcon}
                                                    alt="WPIcon"
                                                    style={{
                                                        width: isSmallMobile ? '14px' : '16px',
                                                        height: isSmallMobile ? '14px' : '16px',
                                                    }}
                                                />
                                            }
                                        >
                                            {isSmallMobile
                                                ? t('ClientDetail.contactBtn').substring(0, 8)
                                                : t('ClientDetail.contactBtn')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            // Tablet/Desktop: Original layout
                            <Grid item xs={12} lg={7}>
                                <Grid item {...getActionButtonsGridProps()}>
                                    <Link component={RouterLink} to={routes.createOrder}>
                                        <Button
                                            type="button"
                                            variant="contained"
                                            color="primary"
                                            size={getButtonSize()}
                                            style={getButtonStyles()}
                                            startIcon={<AddOutlinedIcon />}
                                        >
                                            {t('Order.create')}
                                        </Button>
                                    </Link>
                                </Grid>
                                <Grid item {...getActionButtonsGridProps()}>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        onClick={handleClickContact}
                                        color="primary"
                                        size={getButtonSize()}
                                        style={getButtonStyles()}
                                        startIcon={<img src={WPIcon} alt="WPIcon" />}
                                    >
                                        {t('ClientDetail.contactBtn')}
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>

                    {/* Back Link */}
                    <Box
                        style={{
                            marginTop: isMobile ? '12px' : '8px',
                            marginBottom: isMobile ? '12px' : '8px',
                        }}
                    >
                        <Link
                            component={RouterLink}
                            to={routes.clientPage}
                            style={{
                                fontSize: isMobile ? '14px' : '16px',
                                textDecoration: 'none',
                            }}
                        >
                            ← {t('ClientTitle')}
                        </Link>
                    </Box>

                    {gettingClient && <LinearProgress />}
                </Grid>
            </Grid>

            {!gettingClient && clientData && (
                <>
                    {/* Main Content Area */}
                    <Grid item xs={12} lg={7}>
                        <ClientStatus quantity={clientData?.quantity} total={clientData?.total} />
                        <ClientOrders orders={clientData?.orders} />
                    </Grid>

                    {/* Sidebar Content */}
                    <Grid
                        item
                        xs={12}
                        lg={5}
                        style={{
                            marginBottom: isTabletOrMobile ? (isMobile ? 60 : 100) : 0,
                        }}
                    >
                        <ClientInfo
                            clientId={clientData?.id}
                            fullname={`${clientData?.first_name} ${clientData?.last_name}`}
                            first_name={clientData?.first_name}
                            last_name={clientData?.last_name}
                            phone_number={clientData?.phone_number}
                            email={clientData?.email}
                            dni={clientData?.dni}
                            document_type={clientData?.document_type}
                            address={clientData?.address}
                            address_detail={clientData?.address_line}
                            city_id={clientData?.city_id}
                            city={clientData?.city}
                            handleClickContact={handleClickContact}
                        />
                        <ClientNotes clientId={clientData?.id} notes={clientData?.notes} />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

const mapStateToProps = ({ auth, clients, common }) => ({
    storeId: auth.storeId,
    clientToEdit: clients.clientToEdit,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setClientToEdit,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ClientInfoPage);
